import { composeProgress, isLoading } from "apollo/utils";
import { activateDTAAttachments } from "core/model/utils/featureFlags";
import {
  AllPatientForms,
  FileHISStateful,
  FileState,
  Filev2,
  QueryProgress,
} from "core/types";
import { useMemo } from "react";
import { useTranslations } from "translations";
import { convertFormsToFiles, isFileFormUploaded } from "./utils";

export function useMergedFiles({
  allForms,
  auctionId,
  availableFiles,
  getFilesHISProgress,
  getFilesProgress,
  getFormsProgress,
  isEligiblePage,
  uploadedFiles,
}: {
  allForms: AllPatientForms | undefined;
  isEligiblePage: boolean;
  availableFiles: FileHISStateful[] | null;
  auctionId: number;
  getFilesHISProgress: QueryProgress;
  getFilesProgress: QueryProgress;
  uploadedFiles: Filev2[] | null;
  getFormsProgress: QueryProgress;
}) {
  const translations = useTranslations();
  const areFormsEnabled = activateDTAAttachments && !isEligiblePage;

  const formsAsFiles = useMemo(() => {
    return areFormsEnabled
      ? convertFormsToFiles({
          allFileForms: allForms ?? {},
          auctionId,
          translations,
        })
      : null;
  }, [allForms, auctionId, areFormsEnabled]);

  const uploadedFilesV2 = useMemo<FileState[] | null>(() => {
    const isStillLoading = isLoading(
      composeProgress(
        [
          getFilesProgress,
          getFilesHISProgress,
          ...(areFormsEnabled ? [getFormsProgress] : []),
        ],
        true,
      ),
    );

    if (isStillLoading) {
      return null;
    }

    if (
      uploadedFiles === null &&
      availableFiles === null &&
      formsAsFiles === null
    ) {
      return null;
    }

    const finalUploadedFiles = uploadedFiles ?? [];
    const finalAvailableFiles = availableFiles ?? [];
    const finalFormsAsFiles = formsAsFiles ?? [];

    const updatedFiles = finalUploadedFiles.map((uploadedFile) => {
      if (isFileFormUploaded(uploadedFile)) {
        const matchingAvailableFile = finalFormsAsFiles.find(
          ({ recare_file_type }) =>
            recare_file_type === uploadedFile.recare_file_type,
        );

        if (!matchingAvailableFile) {
          return {
            ...uploadedFile,
            isFileDeleted: true,
          };
        }

        return matchingAvailableFile.updated_at &&
          matchingAvailableFile.updated_at > uploadedFile.created_at
          ? {
              ...uploadedFile,
              isNewVersionAvailable: true,
              updated_at: matchingAvailableFile.updated_at,
            }
          : { ...uploadedFile, isNewVersionAvailable: false };
      }

      const matchingAvailableFile = finalAvailableFiles.find(
        ({ id }) => id === uploadedFile.kis_document_id_string,
      );

      return matchingAvailableFile &&
        matchingAvailableFile.updated_at > uploadedFile.created_at
        ? {
            ...uploadedFile,
            isNewVersionAvailable: true,
            updated_at: matchingAvailableFile.updated_at,
          }
        : { ...uploadedFile, isNewVersionAvailable: false };
    });

    const filteredAvailableFiles = finalAvailableFiles.filter(
      ({ id }) =>
        !finalUploadedFiles.some((file) => file.kis_document_id_string === id),
    );

    const filteredFormsAsFiles = finalFormsAsFiles.filter(
      ({ recare_file_type }) =>
        !finalUploadedFiles.some(
          (file) =>
            file.recare_file_type && file.recare_file_type === recare_file_type,
        ),
    );

    return [
      ...updatedFiles,
      ...filteredAvailableFiles,
      ...filteredFormsAsFiles,
    ];
  }, [
    getFilesProgress,
    getFilesHISProgress,
    getFormsProgress,
    uploadedFiles,
    availableFiles,
    formsAsFiles,
    areFormsEnabled,
    auctionId,
  ]);

  return {
    formsAsFiles,
    queryProgress: composeProgress([
      getFilesProgress,
      getFilesHISProgress,
      getFormsProgress,
    ]),
    uploadedFilesV2,
  };
}
